import { Select, TextField } from '@blueprinthq/joy'
import { Box, Button, ModalBody, ModalContent, VStack } from '@chakra-ui/react'
import { BPDateTimePicker } from '@components'
import { CLIENT_PRONOUNS } from '@constants/clientPronouns'
import { YupPhone } from '@constants/formValidation'
import { Field, Form, Formik } from 'formik'
import React, { useMemo } from 'react'
import InputMask from 'react-input-mask'
import * as Yup from 'yup'
import { useCalendarEvent } from '../../context/calendar-event.context'
import { EventModalFooter } from './event-modal-footer'
import { EventModalHeader } from './event-modal-header'

interface CreateClientModalProps {
  onSubmit: (values: CreateClientFormValues) => void
  onClose: () => void
}

export interface CreateClientFormValues {
  firstName: string
  lastName: string
  pronoun?: string | null
  dateOfBirth: string | null
  email?: string
  phoneNumber?: string
}

const pronounTypes = Object.keys(CLIENT_PRONOUNS)

export const CreateClientModal = ({
  onSubmit,
  onClose
}: CreateClientModalProps) => {
  const handleSubmit = async (values: CreateClientFormValues) => {
    onSubmit(values)
  }
  const { currentPickerValue } = useCalendarEvent()

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(),
    lastName: Yup.string().required(),
    pronoun: Yup.string().nullable().optional(),
    dateOfBirth: Yup.date().max(new Date()).nullable(),
    email: Yup.string().email().optional(),
    phoneNumber: YupPhone.optional()
  })

  const getFirstNameFromPickerInput = useMemo(() => {
    if (currentPickerValue?.inputText) {
      return currentPickerValue.inputText.split(' ')[0]
    }
    return ''
  }, [currentPickerValue])
  const getLastNameFromPickerInput = useMemo(() => {
    if (currentPickerValue?.inputText) {
      return currentPickerValue.inputText.split(' ').slice(1).join(' ')
    }
    return ''
  }, [currentPickerValue])
  const initialValues = useMemo(
    () => ({
      firstName: getFirstNameFromPickerInput,
      lastName: getLastNameFromPickerInput,
      pronoun: null,
      dateOfBirth: '',
      email: undefined,
      phoneNumber: undefined
    }),
    [getFirstNameFromPickerInput, getLastNameFromPickerInput]
  )

  return (
    <ModalContent>
      <Formik<CreateClientFormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          errors,
          touched,
          isValid,
          dirty,
          isSubmitting,
          setFieldValue
        }: any) => (
          <Form style={{ width: '100%' }}>
            <EventModalHeader showCloseButton={true} onClose={onClose}>
              Add Client
            </EventModalHeader>
            <ModalBody p="16px 24px">
              <VStack spacing={4} alignItems="flex-start" width="100%">
                <Field name="firstName">
                  {({ field }: { field: any }) => (
                    <TextField
                      {...field}
                      label="First name"
                      isInvalid={errors.firstName && touched.firstName}
                      isRequired
                      width="100%"
                      bg="white"
                    />
                  )}
                </Field>
                <Field name="lastName">
                  {({ field }: { field: any }) => (
                    <TextField
                      {...field}
                      label="Last name"
                      isInvalid={errors.lastName && touched.lastName}
                      isRequired
                      width="100%"
                      bg="white"
                    />
                  )}
                </Field>
                <Box display="flex" width="100%" gap={2}>
                  <Field name="pronoun">
                    {({ field, form }: { field: any; form: any }) => (
                      <Select
                        label="Pronouns"
                        borderColor="lightgrey"
                        fontSize="medium"
                        value={field.value}
                        onChange={value => form.setFieldValue('pronoun', value)}
                        isInvalid={!!(errors.pronoun && touched.pronoun)}
                        options={pronounTypes}
                        width="100%"
                        bg="white"
                      />
                    )}
                  </Field>
                  <Field name="dateOfBirth">
                    {({ field, form }: { field: any; form: any }) => (
                      <BPDateTimePicker
                        {...field}
                        showDate
                        label="Date of birth"
                        isInvalid={errors.dateOfBirth && touched.dateOfBirth}
                        width="100%"
                        bg="white"
                        onChange={(e: any) =>
                          form.setFieldValue('dateOfBirth', e.target.value)
                        }
                      />
                    )}
                  </Field>
                </Box>
                <Box display="flex" width="100%" gap={2}>
                  <Field name="email">
                    {({ field }: { field: any }) => (
                      <TextField
                        {...field}
                        label="Email"
                        isInvalid={errors.email && touched.email}
                        width="100%"
                        bg="white"
                      />
                    )}
                  </Field>
                  <Field name="phoneNumber">
                    {({ field, form }: { field: any; form: any }) => (
                      <InputMask
                        mask="(999) 999-9999"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      >
                        {(inputProps: any) => (
                          <TextField
                            {...inputProps}
                            {...field}
                            label="Mobile"
                            isInvalid={
                              errors.phoneNumber && touched.phoneNumber
                            }
                            width="100%"
                            bg="white"
                          />
                        )}
                      </InputMask>
                    )}
                  </Field>
                </Box>
              </VStack>
            </ModalBody>
            <EventModalFooter>
              <Button
                type="submit"
                width="100%"
                borderRadius="8px"
                fontSize="16px"
                isDisabled={!isValid || isSubmitting}
                isLoading={isSubmitting}
                data-click-text="Add client"
              >
                Add client
              </Button>
            </EventModalFooter>
          </Form>
        )}
      </Formik>
    </ModalContent>
  )
}
